import { FC } from "react";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { tableCellHeadStyle } from "../../common/styles/tableStyle";
import { TableFooterCommon } from "../../common/components/TableFooterCommon";
import { EditButton } from "../../common/components/EditButton";
import { DeleteTaskCounterSubmission } from "./DeleteTaskCounterSubmission";
import { TableCommonProps } from "../../common/types/tableType";
import { TaskCounterSubmissionListItem } from "../types/TaskCounterTypes";
import dayjs from "dayjs";

export const TaskCounterSubmissionTable: FC<TableCommonProps & { isAdmin?: boolean }> = ({
  data,
  page,
  rowsPerPage,
  totalRecords,
  handleChangePage,
  handleChangeRowsPerPage,
  setData,
  isLoading,
  isAdmin,
  setTotalRecords,
}) => {
  const navigate = useNavigate();

  const handleEdit = (row: TaskCounterSubmissionListItem) => {
    const dateStr = row.workDate ? row.workDate.toString().substring(0, 10) : dayjs().format("YYYY-MM-DD");
    navigate(`/task-counter-screen?date=${dateStr}&focusId=${row.id}`);
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label="task counter submission table">
        <TableHead sx={{ backgroundColor: "#F5F4F4" }}>
          <TableRow>
            <TableCell sx={{ minWidth: "80px", fontWeight: 600 }}>Task ID</TableCell>
            {isAdmin && <TableCell sx={{ fontWeight: 600, minWidth: "170px" }}>User Name</TableCell>}
            <TableCell sx={{ fontWeight: 600, minWidth: "170px" }}>Task Name</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "150px" }}>Date Completed</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "150px" }}>Value</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "150px" }}>Unit of Measure</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "200px" }}>Comment</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "170px" }}>Last Modified Time</TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: "150px" }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>

        {isLoading ? (
          <TableBody>
            <TableRow style={{ height: 300 }}>
              <TableCell colSpan={isAdmin ? 9 : 8} sx={{ textAlign: "center" }}>
                <CircularProgress sx={{ color: "#203c77" }} />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {(data ?? []).length > 0
              ? data.map((row: TaskCounterSubmissionListItem) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    {isAdmin && <TableCell width={"140"} sx={{ textTransform: "lowercase" }}>{row.createdBy}</TableCell>}
                    <TableCell width={"160"}>{row.taskName}</TableCell>
                    <TableCell width={"140"}>
                      {row.workDate ? dayjs(row.workDate.toString().substring(0, 10)).format("MM/DD/YYYY") : ""}
                    </TableCell>
                    <TableCell width={"80"}>{row.value}</TableCell>
                    <TableCell width={"120"}>{row.unitOfMeasure}</TableCell>
                    <TableCell width={"200"} sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span title={row.comment ?? ""}>{row.comment ?? ""}</span>
                    </TableCell>
                    <TableCell width={"160"}>
                      {row.lastModifiedTime
                        ? dayjs(row.lastModifiedTime).format("MM/DD/YYYY HH:mm:ss")
                        : ""}
                    </TableCell>
                    <TableCell width={"100"} align="center">
                      <div>
                        <EditButton
                          id={row.id.toString()}
                          onHandleEdit={() => handleEdit(row)}
                        />
                        <DeleteTaskCounterSubmission
                          id={row.id}
                          info={row.taskName ?? ""}
                          tasks={data}
                          setTasks={setData}
                          setTotalRecords={setTotalRecords}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              : null}
          </TableBody>
        )}

        <TableFooterCommon
          data={data}
          rowsPerPage={rowsPerPage}
          page={page}
          totalRecords={totalRecords}
          onHandleChangePage={handleChangePage}
          onHandleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Table>
    </TableContainer>
  );
};
